import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import useLocationSearch from "../../hooks/useLocationSearch";
import useLocationDetails from "../../hooks/useLocationDetails";
import useGeocodeAddress from "../../hooks/useGeocodeAddress";
import { LocationSearch, LocationDetails } from "../../__generated__/graphql";
import { MapPinIcon, PencilIcon } from "@heroicons/react/24/outline";

interface LocationSearchPickerProps {
  onLocationSelect?: (locationDetails: LocationDetails) => void;
  placeholder?: string;
  className?: string;
}

export default function LocationSearchPicker({
  onLocationSelect,
  placeholder = "Rechercher un lieu avec Google Maps",
  className = "",
}: LocationSearchPickerProps) {
  const { search, searchTerm, results, loading, error } = useLocationSearch();
  const {
    getLocationDetails,
    locationDetails,
    loading: detailsLoading,
    error: detailsError,
  } = useLocationDetails();
  const {
    geocodeAddress,
    geocodedLocation,
    loading: geocodeLoading,
    error: geocodeError,
  } = useGeocodeAddress();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] =
    useState<LocationSearch | null>(null);
  const [isUsingCustomAddress, setIsUsingCustomAddress] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownPortalRef = useRef<HTMLDivElement>(null);

  // Calculate dropdown position based on input position
  const updateDropdownPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4, // 4px for mt-1 equivalent
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  };

  // Update dropdown position when it opens or window resizes/scrolls
  useEffect(() => {
    if (isDropdownOpen) {
      updateDropdownPosition();

      const handleResize = () => updateDropdownPosition();
      const handleScroll = () => updateDropdownPosition();

      window.addEventListener("resize", handleResize);
      window.addEventListener("scroll", handleScroll, true);

      return () => {
        window.removeEventListener("resize", handleResize);
        window.removeEventListener("scroll", handleScroll, true);
      };
    }
  }, [isDropdownOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        dropdownPortalRef.current &&
        !dropdownPortalRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Show dropdown when there's a search term (for results, loading, error, or custom address option)
  useEffect(() => {
    const shouldShowDropdown = !!searchTerm.trim();
    setIsDropdownOpen(shouldShowDropdown);
  }, [results, loading, error, searchTerm]);

  // Handle when location details are received (from Google Places)
  useEffect(() => {
    if (locationDetails && onLocationSelect) {
      onLocationSelect(locationDetails);
    }
  }, [locationDetails, onLocationSelect]);

  // Handle when geocoded location is received (from custom address)
  useEffect(() => {
    if (geocodedLocation && isUsingCustomAddress && onLocationSelect) {
      // Convert GeocodedLocation to LocationDetails format
      const locationDetailsFromGeocode: LocationDetails = {
        name: searchTerm, // Use the user's input as the name
        address: geocodedLocation.address,
        latitude: geocodedLocation.latitude,
        longitude: geocodedLocation.longitude,
        zipcode: geocodedLocation.zipcode,
        city: geocodedLocation.city,
        countryCode: geocodedLocation.countryCode,
        photoUrls: [], // No photos for custom addresses
      };
      onLocationSelect(locationDetailsFromGeocode);
      setIsUsingCustomAddress(false);
    }
  }, [geocodedLocation, isUsingCustomAddress, onLocationSelect, searchTerm]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    search(value);

    // Clear selected location if user is typing
    if (selectedLocation && value !== selectedLocation.address) {
      setSelectedLocation(null);
    }
  };

  const handleLocationSelect = (location: LocationSearch) => {
    setSelectedLocation(location);
    search(location.address);
    setIsDropdownOpen(false);
    setIsUsingCustomAddress(false);

    // Fetch detailed location information using the placeId
    getLocationDetails(location.placeId);
  };

  const handleCustomAddressSelect = () => {
    if (!searchTerm.trim()) return;

    setIsDropdownOpen(false);
    setIsUsingCustomAddress(true);

    // Geocode the custom address to get coordinates
    geocodeAddress(searchTerm.trim());
  };

  const handleInputFocus = () => {
    if (searchTerm.trim()) {
      setIsDropdownOpen(true);
    }
  };

  const isLoading = loading || detailsLoading || geocodeLoading;
  const hasError = error || detailsError || geocodeError;

  const dropdownContent = isDropdownOpen && dropdownPosition && (
    <div
      ref={dropdownPortalRef}
      className="fixed z-[250] bg-input border border-border rounded-md shadow-lg max-h-60 overflow-y-auto"
      style={{
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        width: `${dropdownPosition.width}px`,
      }}
    >
      {isLoading && (
        <div className="px-4 py-3 text-sm text-primary flex items-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b border-border mr-2" />
          {detailsLoading
            ? "Récupération des détails..."
            : geocodeLoading
            ? "Géolocalisation de l'adresse..."
            : "Recherche en cours..."}
        </div>
      )}

      {hasError && !isLoading && (
        <div className="px-4 py-3 text-sm text-red-600">
          {detailsError
            ? "Erreur lors de la récupération des détails."
            : geocodeError
            ? "Erreur lors de la géolocalisation de l'adresse."
            : "Erreur lors de la recherche."}{" "}
          Veuillez réessayer.
        </div>
      )}

      {!isLoading && !hasError && searchTerm.trim() && (
        <>
          {/* Google Places results */}
          {results.length > 0 && (
            <>
              {results.map((location, index) => (
                <button
                  key={`${location.address}-${index}`}
                  onClick={() => handleLocationSelect(location)}
                  className="w-full flex items-center gap-2 px-4 py-3 text-left text-sm hover:bg-card focus:bg-card focus:outline-none border-b border-border cursor-pointer"
                >
                  <MapPinIcon className="w-4 h-4 text-secondary flex-shrink-0" />
                  <div className="font-medium text-primary">
                    {location.address}
                  </div>
                </button>
              ))}
            </>
          )}

          {/* Custom address option - always show when there's a search term */}
          <button
            onClick={handleCustomAddressSelect}
            className="w-full flex items-center gap-2 px-4 py-3 text-left text-sm hover:bg-card focus:bg-card focus:outline-none border-t border-border cursor-pointer bg-card/30"
          >
            <PencilIcon className="w-4 h-4 text-accent flex-shrink-0" />
            <div className="flex flex-col">
              <div className="font-medium text-accent">
                Utiliser cette adresse personnalisée
              </div>
              <div className="text-xs text-secondary">"{searchTerm}"</div>
            </div>
          </button>
        </>
      )}
    </div>
  );

  return (
    <>
      <div className={`relative ${className}`} ref={dropdownRef}>
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          className="w-full p-2 rounded-md bg-input focus:outline-none focus:ring-2 focus:ring-card border border-border placeholder:text-tertiary"
        />
      </div>
      {typeof document !== "undefined" &&
        createPortal(dropdownContent, document.body)}
    </>
  );
}
